import React from "react";
import { LucideIcon, X } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { Button } from "@/ui-components/ui/button";

export interface PageInfoBannerProps {
  title?: string;
  description: React.ReactNode;
  icon: LucideIcon;
  variant?: "blue" | "purple" | "red" | "yellow" | "orange";
  ctaLabel?: string;
  onCtaClick?: () => void;
  infoButton?: React.ReactNode;
  onClose?: () => void;
  className?: string;
}

export const PageInfoBanner: React.FC<PageInfoBannerProps> = ({
  title,
  description,
  icon: Icon,
  variant = "blue",
  ctaLabel,
  onCtaClick,
  infoButton,
  onClose,
  className,
}) => {
  const [isExiting, setIsExiting] = React.useState(false);

  const handleClose = () => {
    setIsExiting(true);
    // Wait for animation to finish (approx 300ms)
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const styles = {
    blue: {
      wrapper: "bg-blue-50/50 border-blue-100 shadow-blue-500/[0.03]",
      iconBg: "bg-blue-100/60 border-blue-200/50",
      iconColor: "text-blue-600",
      title: "text-blue-950",
      text: "text-blue-700/80",
      cta: "bg-blue-600 hover:bg-blue-700 shadow-blue-500/10",
      close: "hover:bg-blue-100/50 text-blue-400 hover:text-blue-600",
    },
    purple: {
      wrapper: "bg-purple-50/50 border-purple-100 shadow-purple-500/[0.03]",
      iconBg: "bg-purple-100/60 border-purple-200/50",
      iconColor: "text-purple-600",
      title: "text-purple-950",
      text: "text-purple-700/80",
      cta: "bg-purple-600 hover:bg-purple-700 shadow-purple-500/10",
      close: "hover:bg-purple-100/50 text-purple-400 hover:text-purple-600",
    },
    red: {
      wrapper: "bg-red-50/50 border-red-100 shadow-red-500/[0.03]",
      iconBg: "bg-red-100/60 border-red-200/50",
      iconColor: "text-red-600",
      title: "text-red-950",
      text: "text-red-700/80",
      cta: "bg-red-600 hover:bg-red-700 shadow-red-500/10",
      close: "hover:bg-red-100/50 text-red-400 hover:text-red-600",
    },
    yellow: {
      wrapper: "bg-yellow-50/50 border-yellow-100 shadow-yellow-500/[0.03]",
      iconBg: "bg-yellow-100/60 border-yellow-200/50",
      iconColor: "text-yellow-600",
      title: "text-yellow-950",
      text: "text-yellow-700/80",
      cta: "bg-yellow-600 hover:bg-yellow-700 shadow-yellow-500/10",
      close: "hover:bg-yellow-100/50 text-yellow-400 hover:text-yellow-600",
    },
    orange: {
      wrapper: "bg-orange-50/50 border-orange-100 shadow-orange-500/[0.03]",
      iconBg: "bg-orange-100/60 border-orange-200/50",
      iconColor: "text-orange-600",
      title: "text-orange-950",
      text: "text-orange-700/80",
      cta: "bg-orange-600 hover:bg-orange-700 shadow-orange-500/10",
      close: "hover:bg-orange-100/50 text-orange-400 hover:text-orange-600",
    },
  };

  const currentStyle = styles[variant];

  return (
    <div
      className={cn(
        "p-6 border rounded-[24px] flex items-start gap-5 shadow-sm transition-all w-full max-w-7xl mx-auto",
        isExiting 
          ? "animate-out fade-out zoom-out-95 slide-out-to-top-2 duration-300 fill-mode-forwards" 
          : "animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-300",
        currentStyle.wrapper,
        className
      )}
    >
      <div
        className={cn(
          "h-11 w-11 rounded-xl border flex items-center justify-center flex-shrink-0 mt-0.5",
          currentStyle.iconBg,
          currentStyle.iconColor
        )}
      >
        <Icon className="h-5 w-5" />
      </div>

      <div className="flex-1 flex flex-col sm:flex-row sm:items-start justify-between gap-6">
        <div className="flex flex-col gap-1.5">
          {title && (
            <h3 className={cn("text-[17px] font-bold tracking-tight leading-tight", currentStyle.title)}>
              {title}
            </h3>
          )}
          <div className={cn("text-[14px] font-medium leading-relaxed opacity-90", currentStyle.text)}>
            {description}
          </div>
          {infoButton && (
            <div className="mt-1">
              {infoButton}
            </div>
          )}
        </div>

        {(ctaLabel || onClose) && (
          <div className="flex items-center gap-3 shrink-0 sm:self-center w-full sm:w-auto justify-between sm:justify-end mt-2 sm:mt-0">
            {ctaLabel && (
              <Button
                onClick={onCtaClick}
                className={cn(
                  "text-white px-6 h-10 text-[12px] font-bold rounded-xl shadow-md transition-all whitespace-nowrap active:scale-95",
                  currentStyle.cta
                )}
              >
                {ctaLabel}
              </Button>
            )}
            {onClose && (
              <button
                type="button"
                onClick={handleClose}
                className={cn(
                  "h-10 w-10 flex items-center justify-center rounded-xl transition-all",
                  currentStyle.close,
                  !ctaLabel && "ml-auto"
                )}
                title="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
